/**
 * Approval Types
 *
 * Type definitions for the approval workflow feature.
 * Handles document approval requests, decisions, and status tracking.
 */

// ============================================================================
// Enums
// ============================================================================

/**
 * Possible states of an approval workflow
 */
export enum ApprovalStatus {
  /** Approval workflow is active, waiting for approver decisions */
  PENDING = 'pending',

  /** All required approvers have approved */
  APPROVED = 'approved',

  /** One or more approvers have rejected */
  REJECTED = 'rejected',

  /** Approval submission was cancelled/withdrawn */
  CANCELLED = 'cancelled',
}

/**
 * Rules for how approval decisions are evaluated
 */
export enum ApprovalRule {
  /** All assigned approvers must approve (unanimous) */
  ALL = 'all',

  /** Any single approver can approve (first to approve wins) */
  ANY = 'any',
}

// ============================================================================
// Frontend Types
// ============================================================================

/**
 * Approval workflow for a document revision
 *
 * NOTE: This type assumes backend enhancements are implemented.
 * See research/backend-updates-required.md for details.
 */
export type Approval = {
  /** Unique identifier for this approval workflow */
  id: string;

  /** Current approval status */
  status: ApprovalStatus;

  /** ID of the document/resource being approved */
  resourceId: string;

  /** Type of resource (always "document" for MVP) */
  resourceType: string;

  /** Parent document ID */
  documentId?: string;

  /** Parent document name */
  documentName?: string;

  /** Human-readable document ID (e.g., "SOP-001") */
  displayId?: string;

  /** When the approval workflow was created */
  createdAt?: string;

  /** When the approval workflow was completed (approved or rejected) */
  completedAt?: string;

  /** List of user IDs assigned as approvers (from backend enhancement) */
  approverIds: string[];

  /** Approval rule: "all" (unanimous) or "any" (single approver) (from backend enhancement) */
  approvalRule: ApprovalRule;

  /** ID of user who submitted for approval (from backend enhancement) */
  submittedBy?: string;

  /** Audit trail of all approval events (submitted, approved, rejected, etc.) */
  auditTrail?: ApprovalEventResponse[];
};

// ============================================================================
// Request Types (snake_case for API)
// ============================================================================

/**
 * Request payload for submitting a document for approval
 *
 * Backend endpoint: POST /api/v1/vault/resources/{resource_id}/approvals
 *
 * @example
 * ```typescript
 * const request: SubmitApprovalRequest = {
 *   approver_ids: [qualityUser1.id, qualityUser2.id],
 *   approval_rule: 'all',
 *   comment: 'Ready for quality review - updated sections 3 and 5'
 * };
 * ```
 */
export type SubmitApprovalRequest = {
  /** List of user IDs who will approve this document (min 1 required) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  approver_ids: string[];

  /** Approval rule (default: "all" for unanimous approval) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  approval_rule: 'all' | 'any';

  /** Optional comment about why submitted for approval */
  comment?: string;
};

/**
 * Request payload for making an approval decision
 *
 * Backend endpoint: POST /api/v1/vault/resources/{resource_id}/approvals/decision
 *
 * @example
 * ```typescript
 * const decision: ApprovalDecisionRequest = {
 *   decision: 'approved',
 *   comment: 'Technical review complete - all requirements met',
 *   signature_name: user.name,
 *   signature_confirmed: true
 * };
 * ```
 */
export type ApprovalDecisionRequest = {
  /** Approval decision */
  decision: 'approved' | 'rejected';

  /** Comment explaining the decision (REQUIRED per requirements) */
  comment: string;

  /** User's typed full name for electronic signature (from backend enhancement) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  signature_name?: string;

  /** Whether user confirmed the electronic signature checkbox (from backend enhancement) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  signature_confirmed?: boolean;
};

/**
 * Request payload for reassigning approvers
 *
 * Backend endpoint: PUT /api/v1/vault/resources/{resource_id}/approvals/approvers
 * NOTE: API route may not be registered in initial MVP (placeholder UI only)
 */
export type ReassignApproversRequest = {
  /** New list of user IDs who will approve (replaces existing approvers) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  approver_ids: string[];
};

// ============================================================================
// Response Types (snake_case from API)
// ============================================================================

/**
 * Approval event from audit trail
 *
 * Represents a single action in the approval workflow audit trail.
 */
export type ApprovalEventResponse = {
  /** Unique identifier for this event */
  id: string;
  /** Action type: 'submitted', 'approved', 'rejected', 'cancelled', 'reassigned' */
  action: string;
  /** Optional comment explaining the action (required for rejections) */
  comment: string | undefined;
  /** User ID who performed the action */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  created_by: string;
  /** Timestamp when action occurred (ISO string) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  created_at: string;
};

/**
 * Response from approval API endpoints
 *
 * This type reflects the ENHANCED backend response (with backend improvements).
 * See research/backend-apis.md for details.
 *
 * @remarks
 * If backend enhancements are NOT available:
 * - approver_ids will be undefined (store in local state)
 * - approval_rule will be undefined (assume "all")
 * - submitted_by will be undefined (cannot display)
 */
export type ApprovalResponse = {
  id: string;
  status: string; // "pending" | "approved" | "rejected"
  // eslint-disable-next-line @typescript-eslint/naming-convention
  resource_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  resource_type: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  document_id?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  document_name?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  display_id?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  created_at?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  completed_at?: string;

  // FROM BACKEND ENHANCEMENTS:
  /** List of assigned approvers */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  approver_ids?: string[];
  /** Approval rule ("all" | "any") */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  approval_rule?: string;
  /** Who submitted for approval */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  submitted_by?: string;
  /** Audit trail of all approval events (submitted, approved, rejected, etc.) */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  audit_trail?: ApprovalEventResponse[];
};

/**
 * Response from GET /approvals/pending endpoint (from backend)
 *
 * Returns paginated list of approvals with snake_case
 */
export type ApprovalListResponse = {
  items: ApprovalResponse[];
  total: number;
  limit: number;
  offset: number;
};

/**
 * Frontend approval list (camelCase)
 */
export type ApprovalList = {
  items: Approval[];
  total: number;
  limit: number;
  offset: number;
};

// ============================================================================
// Frontend-Only Types
// ============================================================================

/**
 * Electronic signature data (frontend-only for MVP)
 *
 * This data is validated on the frontend and optionally sent to backend
 * if backend enhancement is available.
 *
 * @example
 * ```typescript
 * const isSignatureValid = (signature: ElectronicSignature, user: User): boolean => {
 *   return signature.signatureName.trim() === user.name && signature.confirmed;
 * };
 * ```
 */
export type ElectronicSignature = {
  /** User's typed full name (must match authenticated user's name) */
  signatureName: string;

  /** User confirmed the signature checkbox */
  confirmed: boolean;

  /** When the signature was captured (frontend timestamp) */
  timestamp: Date;
};

// ============================================================================
// Form Data Types (camelCase for frontend)
// ============================================================================

/**
 * Form data for submit for approval dialog
 */
export type SubmitApprovalFormData = {
  /** Selected approver user IDs */
  approverIds: string[];

  /** Optional submission comment */
  comment?: string;
};

/**
 * Form data for approval/rejection dialog
 */
export type ApprovalDecisionFormData = {
  /** Approval or rejection decision */
  decision: 'approved' | 'rejected';

  /** Required comment explaining decision */
  comment: string;

  /** User's typed full name for signature */
  signatureName: string;

  /** Signature confirmation checkbox */
  signatureConfirmed: boolean;
};

/**
 * Form data for reassign approvers dialog
 */
export type ReassignApproversFormData = {
  /** New selected approver user IDs */
  approverIds: string[];

  /** Optional comment about reassignment */
  comment?: string;
};

// ============================================================================
// Helper Types
// ============================================================================

/**
 * Approver option for select component
 */
export type ApproverOption = {
  /** User ID */
  value: string;
  /** User name */
  label: string;
  /** User email (for display) */
  email: string;
  /** User role (Admin/Quality) */
  role: string;
};
